import { useState, useMemo, useRef, useCallback } from "react";
import { IonPage, IonContent, IonHeader, IonToolbar, IonTitle, IonButtons } from "@ionic/react";
import { Plus, Check, Undo2, Link2 } from "lucide-react";
import { useStore } from "../store/useStore";
import GroupedTaskList from "../components/GroupedTaskList";
import TaskPickerModal from "../components/TaskPickerModal";
import CalendarSidebar from "../components/CalendarSidebar";
import TagFilterPills from "../components/TagFilterPills";
import AddPrerequisiteModal from "../components/AddPrerequisiteModal";
import { useConfirmedComplete } from "../hooks/useConfirmedComplete";
import { deduplicateTasks, filterByTag, sortTasks } from "../lib/task-filters";
import type { ITask } from "../types";

const DOUBLE_TAP_MS = 300;

function CompletedTaskRow({ task, onDoubleTap }: { task: ITask; onDoubleTap: () => void }) {
  const lastTapRef = useRef(0);

  const handleTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < DOUBLE_TAP_MS) {
      onDoubleTap();
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  }, [onDoubleTap]);

  return (
    <div
      className="flex items-center gap-3 py-2 px-3 rounded-lg bg-surface border border-border cursor-pointer active:opacity-70 transition-opacity"
      onClick={handleTap}
    >
      <div className="w-5 h-5 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
        <svg
          className="w-3 h-3 text-success"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <span className="text-sm text-muted-foreground line-through truncate">
        {task.title || task.raw_input}
      </span>
    </div>
  );
}

export default function TodayPage() {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [prereqTarget, setPrereqTarget] = useState<string | null>(null);
  const tasks = useStore((state) => state.tasks);
  const sharedTasks = useStore((state) => state.sharedTasks);
  const uncompleteTask = useStore((state) => state.uncompleteTask);
  const moveToBacklog = useStore((state) => state.moveToBacklog);
  const loading = useStore((state) => state.loading.tasks);
  const activeTagFilter = useStore((state) => state.activeTagFilter);
  const activeSortFilter = useStore((state) => state.activeSortFilter);
  const { requestComplete, modal: completionModal } = useConfirmedComplete();

  const todayTasks = useMemo(() => {
    const ownToday = tasks.filter((t) => t.status === "today");
    const sharedToday = sharedTasks.filter((t) => t.status === "today");
    const filtered = filterByTag(
      deduplicateTasks([...ownToday, ...sharedToday]),
      activeTagFilter
    );
    return sortTasks(filtered, activeSortFilter);
  }, [tasks, sharedTasks, activeTagFilter, activeSortFilter]);

  const doneTasks = useMemo(() => {
    const ownDone = tasks.filter((t) => t.status === "done");
    const sharedDone = sharedTasks.filter((t) => t.status === "done");
    const merged = filterByTag(
      deduplicateTasks([...ownDone, ...sharedDone]),
      activeTagFilter
    );

    return merged
      .sort(
        (a, b) =>
          new Date(b.completed_at || b.updated_at).getTime() -
          new Date(a.completed_at || a.updated_at).getTime()
      )
      .slice(0, 10);
  }, [tasks, sharedTasks, activeTagFilter]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Today</IonTitle>
          <IonButtons slot="end">
            <button
              onClick={() => setPickerOpen(true)}
              className="p-2 text-primary"
            >
              <Plus className="w-5 h-5" />
            </button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="px-4 pt-3 pb-2 space-y-4">
          <TagFilterPills />

          <CalendarSidebar />

          <section>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 shimmer rounded-lg" />
                ))}
              </div>
            ) : todayTasks.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">
                  Nothing planned for today
                </p>
                <p className="text-muted-foreground text-sm mt-1">
                  Tap + to pick tasks from your backlog
                </p>
              </div>
            ) : (
              <GroupedTaskList
                tasks={todayTasks}
                stackOnlyIfMultiVisible
                showComplete
                showMoveToBacklog
                swipeStart={{
                  label: "Done",
                  icon: <Check className="w-5 h-5" />,
                  bg: "hsl(var(--success))",
                  onAction: (uuid) => requestComplete(uuid),
                }}
                swipeEnd={{
                  label: "Remove",
                  icon: <Undo2 className="w-5 h-5" />,
                  bg: "hsl(var(--muted-foreground))",
                  onAction: (uuid) => moveToBacklog(uuid),
                }}
                swipeEndSecondary={{
                  label: "Prereq",
                  icon: <Link2 className="w-5 h-5" />,
                  bg: "hsl(var(--primary))",
                  onAction: (uuid) => setPrereqTarget(uuid),
                }}
              />
            )}
          </section>

          {doneTasks.length > 0 && (
            <section>
              <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wide">
                Completed
              </h2>
              <div className="space-y-2 opacity-60">
                {doneTasks.map((task) => (
                  <CompletedTaskRow
                    key={task.uuid}
                    task={task}
                    onDoubleTap={() => uncompleteTask(task.uuid)}
                  />
                ))}
              </div>
            </section>
          )}
        </div>

        <TaskPickerModal
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
        />

        {completionModal}

        {prereqTarget &&
          (() => {
            const target = tasks.find((t) => t.uuid === prereqTarget);
            if (!target) return null;
            return (
              <AddPrerequisiteModal
                isOpen={!!prereqTarget}
                onClose={() => setPrereqTarget(null)}
                dependentTask={target}
              />
            );
          })()}
      </IonContent>
    </IonPage>
  );
}
